import React, { useState, useEffect } from 'react';
import axios from 'axios';
import notificationService from '../utils/notifications';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

function UserProfile({ user, onUserUpdate }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [profile, setProfile] = useState({
    email: '',
    notes: '',
    iban: '',
    vat_status: ''
  });
  const [copySuccess, setCopySuccess] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [notificationRequesting, setNotificationRequesting] = useState(false);

  useEffect(() => {
    if (user?.id) {
      fetchProfile();
    }
    checkNotificationPermission();
  }, [user?.id]); // Refetch when user changes or component mounts

  const checkNotificationPermission = () => {
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
    }
  };

  const handleRequestNotificationPermission = async () => {
    setNotificationRequesting(true);
    try {
      const result = await notificationService.requestPermission();
      if (result.success) {
        setNotificationPermission('granted');
        notificationService.showSuccess('Notifications enabled! You will receive notifications for important events.');
      } else {
        setError(result.error || 'Failed to enable notifications');
      }
    } catch (error) {
      setError('Error requesting notification permission: ' + error.message);
    } finally {
      setNotificationRequesting(false);
    }
  };

  const fetchProfile = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_BASE}/user/profile`);
      setProfile({
        email: response.data.email || '',
        notes: response.data.notes || '',
        iban: response.data.iban || '',
        vat_status: response.data.vat_status || ''
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
      setError('Error loading profile: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
    setSuccess('');
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
    setSuccess('');
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.put(`${API_BASE}/user/profile`, profile);
      // Update local profile state with response data
      // Use response vat_status if it has a value, otherwise keep what we sent
      const updatedProfile = {
        email: response.data.email || '',
        notes: response.data.notes || '',
        iban: response.data.iban || '',
        vat_status: response.data.vat_status || profile.vat_status || ''
      };
      setProfile(updatedProfile);
      setSuccess('Profile updated successfully!');
      if (onUserUpdate) {
        onUserUpdate(response.data);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setError('Error updating profile: ' + (error.response?.data?.error || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSave = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setError('New password must be at least 6 characters long');
      return;
    }

    setSaving(true);

    try {
      await axios.put(`${API_BASE}/user/password`, {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });
      setSuccess('Password changed successfully!');
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      console.error('Error changing password:', error);
      setError('Error changing password: ' + (error.response?.data?.error || error.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="user-profile">
        <div className="loading-screen">
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-profile">
      <div className="profile-header">
        <h1>User Profile</h1>
        <p className="profile-subtitle">Manage your account settings</p>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="profile-sections">
        {/* Account Information */}
        <div className="profile-section">
          <h2>Account Information</h2>
          <form onSubmit={handleProfileSave}>
            <div className="form-group">
              <label htmlFor="username">Username</label>
              <input
                type="text"
                id="username"
                value={user?.username || ''}
                disabled
                className="form-input"
              />
              <small className="form-hint">Username cannot be changed</small>
            </div>

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={profile.email}
                onChange={handleProfileChange}
                className="form-input"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="notes">Notes</label>
              <textarea
                id="notes"
                name="notes"
                value={profile.notes}
                onChange={handleProfileChange}
                className="form-input form-textarea"
                rows="6"
                placeholder="Add any personal notes or reminders here..."
              />
            </div>

            {/* IBAN Field for Payment Info */}
            <div className="form-group">
              <label htmlFor="iban">IBAN (Payment Information)</label>
              <div style={{ marginBottom: '0.5rem' }}>
                <small style={{ color: '#999', fontSize: '0.85rem', display: 'block', marginBottom: '0.5rem' }}>
                  Your IBAN will be displayed on exported PDFs to make it easier for clients to pay you.
                  This field is optional and only used to show payment info on documents you export.
                </small>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  type="text"
                  id="iban"
                  name="iban"
                  value={profile.iban}
                  onChange={handleProfileChange}
                  className="form-input"
                  placeholder="e.g., PT50 0000 0000 0000 0000 0000 0"
                  style={{ flex: 1, fontFamily: 'monospace' }}
                />
                <button
                  type="button"
                  onClick={() => {
                    if (profile.iban) {
                      navigator.clipboard.writeText(profile.iban).then(() => {
                        setCopySuccess(true);
                        setTimeout(() => setCopySuccess(false), 2000);
                      }).catch(() => {
                        // Fallback for older browsers
                        const textArea = document.createElement('textarea');
                        textArea.value = profile.iban;
                        document.body.appendChild(textArea);
                        textArea.select();
                        document.execCommand('copy');
                        document.body.removeChild(textArea);
                        setCopySuccess(true);
                        setTimeout(() => setCopySuccess(false), 2000);
                      });
                    }
                  }}
                  className="btn btn-secondary"
                  style={{ whiteSpace: 'nowrap', minWidth: '80px' }}
                  disabled={!profile.iban}
                >
                  {copySuccess ? '✓ Copied!' : 'Copy'}
                </button>
              </div>
            </div>

            {/* VAT Status Field */}
            <div className="form-group">
              <label htmlFor="vat_status">VAT Status</label>
              <div style={{ marginBottom: '0.5rem' }}>
                <small style={{ color: '#999', fontSize: '0.85rem', display: 'block', marginBottom: '0.5rem' }}>
                  Select your VAT status. Regular VAT users will have 23% VAT added to their invoices (on base revenue + additional charges). 
                  VAT Exempt users will have "VAT Exempt" notation on their PDFs.
                </small>
              </div>
              <div style={{ 
                display: 'flex', 
                gap: '1rem', 
                flexWrap: 'wrap',
                marginTop: '0.5rem'
              }}>
                <button
                  type="button"
                  onClick={() => setProfile(prev => ({ ...prev, vat_status: 'exempt' }))}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    cursor: 'pointer',
                    color: (profile.vat_status || '') === 'exempt' ? '#ffd700' : '#e5e5e5',
                    padding: '0.5rem 1rem',
                    borderRadius: '6px',
                    border: `2px solid ${(profile.vat_status || '') === 'exempt' ? '#ffd700' : '#555'}`,
                    backgroundColor: (profile.vat_status || '') === 'exempt' ? 'rgba(255, 215, 0, 0.15)' : 'transparent',
                    transition: 'all 0.2s',
                    fontWeight: (profile.vat_status || '') === 'exempt' ? '600' : '400',
                    fontSize: '1rem'
                  }}
                >
                  VAT Exempt
                </button>
                <button
                  type="button"
                  onClick={() => setProfile(prev => ({ ...prev, vat_status: 'regular' }))}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    cursor: 'pointer',
                    color: (profile.vat_status || '') === 'regular' ? '#ffd700' : '#e5e5e5',
                    padding: '0.5rem 1rem',
                    borderRadius: '6px',
                    border: `2px solid ${(profile.vat_status || '') === 'regular' ? '#ffd700' : '#555'}`,
                    backgroundColor: (profile.vat_status || '') === 'regular' ? 'rgba(255, 215, 0, 0.15)' : 'transparent',
                    transition: 'all 0.2s',
                    fontWeight: (profile.vat_status || '') === 'regular' ? '600' : '400',
                    fontSize: '1rem'
                  }}
                >
                  Regular VAT (23%)
                </button>
              </div>
            </div>

            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Profile'}
            </button>
          </form>
        </div>

        {/* Change Password */}
        <div className="profile-section">
          <h2>Change Password</h2>
          <form onSubmit={handlePasswordSave}>
            <div className="form-group">
              <label htmlFor="currentPassword">Current Password</label>
              <input
                type="password"
                id="currentPassword"
                name="currentPassword"
                value={passwordForm.currentPassword}
                onChange={handlePasswordChange}
                className="form-input"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                value={passwordForm.newPassword}
                onChange={handlePasswordChange}
                className="form-input"
                required
                minLength="6"
              />
              <small className="form-hint">Must be at least 6 characters long</small>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={passwordForm.confirmPassword}
                onChange={handlePasswordChange}
                className="form-input"
                required
                minLength="6"
              />
            </div>

            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={saving}
            >
              {saving ? 'Changing Password...' : 'Change Password'}
            </button>
          </form>
        </div>

        {/* Notifications */}
        <div className="profile-section">
          <h2>Notifications</h2>
          <p style={{ color: '#999', marginBottom: '1rem', fontSize: '0.9rem' }}>
            Enable browser notifications to receive alerts for important events like upcoming gigs and payment reminders.
            {!('Notification' in window) && (
              <span style={{ color: '#ff9800', display: 'block', marginTop: '0.5rem' }}>
                ⚠️ Your browser does not support notifications.
              </span>
            )}
            {navigator.userAgent.match(/iPhone|iPad|iPod/) && (
              <span style={{ color: '#ff9800', display: 'block', marginTop: '0.5rem' }}>
                ℹ️ iOS Safari does not support background push notifications. Notifications will only work when the app is open.
              </span>
            )}
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
            <div style={{ flex: 1 }}>
              <div style={{ color: '#e5e5e5', marginBottom: '0.25rem', fontWeight: 500 }}>
                Notification Permission
              </div>
              <div style={{ color: '#999', fontSize: '0.85rem' }}>
                Status: {
                  notificationPermission === 'granted' ? (
                    <span style={{ color: '#4caf50' }}>✓ Enabled</span>
                  ) : notificationPermission === 'denied' ? (
                    <span style={{ color: '#ff6b6b' }}>✗ Blocked</span>
                  ) : (
                    <span style={{ color: '#ff9800' }}>Not set</span>
                  )
                }
              </div>
            </div>
            {notificationPermission !== 'granted' && (
              <button
                type="button"
                onClick={handleRequestNotificationPermission}
                disabled={notificationRequesting || notificationPermission === 'denied' || !('Notification' in window)}
                className="btn btn-primary"
                style={{ whiteSpace: 'nowrap' }}
              >
                {notificationRequesting ? 'Requesting...' : notificationPermission === 'denied' ? 'Blocked' : 'Enable Notifications'}
              </button>
            )}
          </div>
          {notificationPermission === 'denied' && (
            <div style={{ 
              padding: '0.75rem', 
              backgroundColor: 'rgba(255, 107, 107, 0.1)', 
              border: '1px solid rgba(255, 107, 107, 0.3)',
              borderRadius: '4px',
              color: '#ff6b6b',
              fontSize: '0.85rem'
            }}>
              Notifications are blocked. To enable them, please:
              <ol style={{ margin: '0.5rem 0 0 1.5rem', padding: 0 }}>
                <li>Click the lock/info icon in your browser's address bar</li>
                <li>Find "Notifications" in the permissions list</li>
                <li>Change it to "Allow"</li>
                <li>Refresh this page</li>
              </ol>
            </div>
          )}
        </div>

        {/* Account Stats */}
        <div className="profile-section">
          <h2>Account Information</h2>
          <div className="profile-stats">
            <div className="profile-stat">
              <span className="stat-label">Account Created:</span>
              <span className="stat-value">
                {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
              </span>
            </div>
            {user?.role && (
              <div className="profile-stat">
                <span className="stat-label">Role:</span>
                <span className="stat-value stat-role">{user.role}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserProfile;

